/* Custom Bundle & Buy widget.
   Adds N units (each with its own variant selection) to the cart in one request
   and hands the response to the theme's cart drawer / notification. */
if (!customElements.get('bundle-buy')) {
  customElements.define(
    'bundle-buy',
    class BundleBuy extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-bundle-product-json]');
        if (!dataEl) return;

        try {
          this.productData = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('[BundleBuy] Invalid product JSON', e);
          return;
        }

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.currency = this.dataset.currency || 'USD';
        this.tiers = Array.from(this.querySelectorAll('.bundle-buy__tier'));
        this.atcButton = this.querySelector('.bundle-buy__atc');
        this.atcPrice = this.querySelector('.bundle-buy__atc-price');
        this.atcText = this.querySelector('.bundle-buy__atc-text');
        this.errorEl = this.querySelector('.bundle-buy__error');
        this.errorText = this.querySelector('.bundle-buy__error-text');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.tiers.forEach((tier) => {
          const radio = tier.querySelector('input[type="radio"]');
          radio?.addEventListener('change', () => this.selectTier(tier));

          tier.querySelectorAll('.bundle-buy__pill').forEach((pill) => {
            pill.addEventListener('click', (evt) => {
              evt.preventDefault();
              this.onPillClick(tier, pill);
            });
          });
        });

        this.atcButton?.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.addToCart();
        });

        this.tiers.forEach((tier) => this.refreshTier(tier));

        const urlTier = new URLSearchParams(window.location.search).get('tier');
        if (urlTier) {
          const urlTarget = this.tiers.find((t) => t.dataset.tierIndex === urlTier);
          const urlRadio = urlTarget?.querySelector('input[type="radio"]');
          if (urlRadio) {
            this.tiers.forEach((t) => {
              const r = t.querySelector('input[type="radio"]');
              if (r) r.checked = r === urlRadio;
            });
          }
        }

        const initiallySelected =
          this.tiers.find((t) => t.querySelector('input[type="radio"]')?.checked) || this.tiers[0];
        if (initiallySelected) {
          const radio = initiallySelected.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;
          this.selectTier(initiallySelected, true);
        }
      }

      /* ----- state helpers ----- */

      unitSelections(unit) {
        return Array.from(unit.querySelectorAll('.bundle-buy__option')).map((optionEl) => {
          const active = optionEl.querySelector('.bundle-buy__pill.is-active');
          return active ? active.dataset.value : null;
        });
      }

      findVariant(options, onlyAvailable) {
        return this.productData.variants.find(
          (v) =>
            (!onlyAvailable || v.available) &&
            v.options.every((value, index) => options[index] === null || value === options[index])
        );
      }

      unitVariant(unit) {
        const options = this.unitSelections(unit);
        return this.findVariant(options, false) || null;
      }

      selectedTier() {
        return this.tiers.find((t) => t.classList.contains('is-selected'));
      }

      /* ----- interactions ----- */

      selectTier(tier, initial = false) {
        this.tiers.forEach((t) => {
          const isSelected = t === tier;
          t.classList.toggle('is-selected', isSelected);
          t.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
        });
        this.hideError();
        this.updateAtc(!initial);
      }

      onPillClick(tier, pill) {
        const optionEl = pill.closest('.bundle-buy__option');
        optionEl.querySelectorAll('.bundle-buy__pill').forEach((p) => {
          p.classList.toggle('is-active', p === pill);
          p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
        });
        this.refreshTier(tier);
        if (tier.classList.contains('is-selected')) {
          this.hideError();
          this.updateAtc(true);
        }
      }

      /* ----- pricing + availability ----- */

      refreshTier(tier) {
        const units = Array.from(tier.querySelectorAll('.bundle-buy__unit'));
        let total = 0;
        let compareTotal = 0;
        let allResolved = true;
        let allAvailable = true;

        units.forEach((unit) => {
          this.refreshUnitAvailability(unit);
          const variant = this.unitVariant(unit);
          if (!variant) {
            allResolved = false;
            return;
          }
          if (!variant.available) allAvailable = false;
          total += variant.price;
          compareTotal += Math.max(variant.compare_at_price || 0, variant.price);
        });

        const discountPercent = parseFloat(tier.dataset.discountPercent || '0');
        let finalTotal = total;
        if (discountPercent > 0) {
          compareTotal = Math.max(compareTotal, total);
          finalTotal = Math.round(total * (1 - discountPercent / 100));
        }

        tier.dataset.total = finalTotal;
        tier.dataset.resolved = allResolved ? 'true' : 'false';
        tier.dataset.available = allAvailable ? 'true' : 'false';

        const priceEl = tier.querySelector('.bundle-buy__tier-price');
        const compareEl = tier.querySelector('.bundle-buy__tier-compare');
        const perUnitEl = tier.querySelector('.bundle-buy__tier-per-unit');

        if (priceEl) priceEl.textContent = this.formatMoney(finalTotal);
        if (compareEl) {
          const showCompare = compareTotal > finalTotal;
          compareEl.hidden = !showCompare;
          if (showCompare) compareEl.textContent = this.formatMoney(compareTotal);
        }
        if (perUnitEl && units.length > 1) {
          perUnitEl.textContent = `${this.formatMoney(Math.round(finalTotal / units.length))} / ${
            perUnitEl.dataset.unitLabel || 'item'
          }`;
        }
      }

      refreshUnitAvailability(unit) {
        const optionEls = Array.from(unit.querySelectorAll('.bundle-buy__option'));
        const selections = this.unitSelections(unit);

        optionEls.forEach((optionEl, optionIndex) => {
          optionEl.querySelectorAll('.bundle-buy__pill').forEach((pill) => {
            const testOptions = selections.slice();
            testOptions[optionIndex] = pill.dataset.value;
            const match = this.findVariant(testOptions, true);
            pill.classList.toggle('is-unavailable', !match);
          });
        });
      }

      updateAtc(animate = false) {
        const tier = this.selectedTier();
        if (!tier || !this.atcButton) return;

        const soldOut = tier.dataset.available !== 'true' || tier.dataset.resolved !== 'true';
        this.atcButton.toggleAttribute('disabled', soldOut);

        if (this.atcText) {
          this.atcText.textContent = soldOut
            ? this.atcText.dataset.soldOutText || 'Sold out'
            : this.atcText.dataset.addText || 'Add to cart';
        }

        if (this.atcPrice) {
          this.atcPrice.textContent = this.formatMoney(parseInt(tier.dataset.total || '0', 10));
          this.atcPrice.parentElement?.classList.toggle('bundle-buy__atc--soldout', soldOut);
          if (animate) {
            this.atcButton.classList.remove('is-price-updating');
            void this.atcButton.offsetWidth;
            this.atcButton.classList.add('is-price-updating');
          }
        }
      }

      /* ----- cart ----- */

      collectItems() {
        const tier = this.selectedTier();
        if (!tier) return [];

        const counts = new Map();
        tier.querySelectorAll('.bundle-buy__unit').forEach((unit) => {
          const variant = this.unitVariant(unit);
          if (variant) counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
        });

        return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
      }

      addToCart() {
        if (this.atcButton.hasAttribute('disabled') || this.atcButton.classList.contains('loading')) return;

        const tier = this.selectedTier();
        const items = this.collectItems();
        if (!tier || items.length === 0) {
          this.showError(this.dataset.errorGeneric || 'Please select your options.');
          return;
        }

        const expectedCount = tier.querySelectorAll('.bundle-buy__unit').length;
        const collectedCount = items.reduce((sum, item) => sum + item.quantity, 0);
        if (collectedCount !== expectedCount) {
          this.showError(this.dataset.errorGeneric || 'Please select your options.');
          return;
        }

        this.hideError();
        this.setLoading(true);

        const body = {
          items,
          sections_url: window.location.pathname,
        };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then(async (response) => {
            let data = {};
            try {
              data = await response.json();
            } catch (_e) {
              data = {};
            }
            if (!response.ok) data.status = data.status || response.status;
            return data;
          })
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || 'Something went wrong. Please try again.');
              return;
            }

            if (!this.cart) {
              window.location = window.routes?.cart_url || '/cart';
              return;
            }

            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'bundle-buy',
                cartData: response,
              });
            }

            fetch('/cart.js')
              .then((r) => r.json())
              .then((cartData) => {
                document.dispatchEvent(
                  new CustomEvent('cart:updated', {
                    detail: {
                      cart: cartData,
                      totalPrice: cartData.total_price,
                      items: cartData.items,
                      timestamp: Date.now(),
                    },
                    bubbles: true,
                  })
                );
              })
              .catch(() => {});

            this.cart.renderContents(response);
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          })
          .catch((e) => {
            console.error('[BundleBuy] addToCart error:', e);
            this.showError('Something went wrong. Please try again.');
          })
          .finally(() => this.setLoading(false));
      }

      setLoading(isLoading) {
        this.atcButton.classList.toggle('loading', isLoading);
        this.atcButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.atcButton.querySelector('.loading-spinner')?.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        if (!this.errorEl) return;
        if (this.errorText) this.errorText.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      formatMoney(cents) {
        if (typeof formatMoneyShopify === 'function') {
          return formatMoneyShopify(cents, this.moneyFormat, this.currency);
        }
        return `$${(cents / 100).toFixed(2)}`;
      }
    }
  );
}
